import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { receiptsAPI, salesAPI, purchasesAPI } from '../../services/garageApi';
import { ArrowLeft, Receipt, AlertCircle } from 'lucide-react';

const CreateReceipt = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [relatedDocuments, setRelatedDocuments] = useState([]);
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        document_type: '',   // 'Sale' or 'Purchase' — UI only
        document_id: '',     // UI only — maps to transaction_id
        amount: '',
        receipt_date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
    });

    const fetchRelatedDocuments = useCallback(async () => {
        try {
            let response;
            switch (formData.document_type) {
                case 'Sale':
                    response = await salesAPI.getAll({ status: 'pending' });
                    break;
                case 'Purchase':
                    response = await purchasesAPI.getAll({ payment_status: 'pending,partial' });
                    break;
                default:
                    return;
            }
            const data = Array.isArray(response.data) ? response.data : [];
            setRelatedDocuments(data);
        } catch (err) {
            console.error('Error fetching related documents:', err);
            setError('Failed to load documents.');
        }
    }, [formData.document_type]);

    useEffect(() => {
        if (formData.document_type) {
            setRelatedDocuments([]);
            setFormData(prev => ({ ...prev, document_id: '', amount: '' }));
            fetchRelatedDocuments();
        }
    }, [fetchRelatedDocuments, formData.document_type]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Auto-populate amount when document is selected
        if (name === 'document_id' && value) {
            const doc = relatedDocuments.find(d => d.id === value);
            if (doc) {
                const total = Number(doc.total_amount || doc.net_amount || 0);
                const paid = Number(doc.total_paid || 0);
                const outstanding = total - paid;
                setFormData(prev => ({
                    ...prev,
                    document_id: value,
                    amount: outstanding > 0 ? outstanding : 0
                }));
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.document_type || !formData.document_id) {
            setError('Please select a document type and document.');
            return;
        }

        if (!formData.amount || Number(formData.amount) <= 0) {
            setError('Amount must be greater than 0.');
            return;
        }

        // Map frontend values to what backend expects
        const type = formData.document_type === 'Purchase' ? 'purchase' : 'sale';

        try {
            setLoading(true);
            await receiptsAPI.create({
                transaction_id: formData.document_id,
                type: type,
                amount: Number(formData.amount),
                receipt_date: formData.receipt_date,
                payment_method: formData.payment_method,
            });
            navigate('/receipts');
        } catch (err) {
            console.error('Error creating receipt:', err);
            const message = err.response?.data?.error || err.response?.data?.message || 'Error creating receipt. Please try again.';
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto">
            <div className="bg-white shadow rounded-lg">
                {/* Header */}
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={() => navigate('/receipts')}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </button>
                        <div className="flex items-center space-x-2">
                            <Receipt className="h-5 w-5 text-blue-600" />
                            <h5 className="text-lg font-semibold">Create New Receipt</h5>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mx-6 mt-4 flex items-center space-x-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Document Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Type</label>
                            <select
                                name="document_type"
                                value={formData.document_type}
                                onChange={handleChange}
                                className="form-select w-full rounded-md border-gray-300"
                                required
                            >
                                <option value="">Select Type</option>
                                <option value="Sale">Sale</option>
                                <option value="Purchase">Purchase</option>
                            </select>
                        </div>

                        {/* Related Document */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Select Transaction</label>
                            <select
                                name="document_id"
                                value={formData.document_id}
                                onChange={handleChange}
                                className="form-select w-full rounded-md border-gray-300"
                                required
                                disabled={!formData.document_type}
                            >
                                <option value="">Select Document</option>
                                {relatedDocuments.map(doc => (
                                    <option key={doc.id} value={doc.id}>
                                        {doc.id} — {doc.customer?.name || doc.supplier?.name || 'Unknown'} — ${Number(doc.total_amount || doc.net_amount || 0).toFixed(2)}
                                    </option>
                                ))}
                            </select>
                            {formData.document_type && relatedDocuments.length === 0 && (
                                <p className="mt-1 text-sm text-gray-500">No unpaid transactions found.</p>
                            )}
                        </div>

                        {/* Receipt Date */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Receipt Date</label>
                            <input
                                type="date"
                                name="receipt_date"
                                value={formData.receipt_date}
                                onChange={handleChange}
                                className="form-input w-full rounded-md border-gray-300"
                                required
                            />
                        </div>

                        {/* Amount */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                            <input
                                type="number"
                                name="amount"
                                value={formData.amount}
                                onChange={handleChange}
                                className="form-input w-full rounded-md border-gray-300"
                                step="0.01"
                                min="0.01"
                                required
                            />
                        </div>

                        {/* Payment Method */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                            <select
                                name="payment_method"
                                value={formData.payment_method}
                                onChange={handleChange}
                                className="form-select w-full rounded-md border-gray-300"
                                required
                            >
                                <option value="cash">Cash</option>
                                <option value="cheque">Cheque</option>
                                <option value="credit_card">Credit Card</option>
                                <option value="bank_transfer">Bank Transfer</option>
                            </select>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="flex justify-end space-x-4 pt-6 border-t">
                        <button
                            type="button"
                            onClick={() => navigate('/receipts')}
                            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={loading}
                        >
                            {loading ? 'Creating...' : 'Create Receipt'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateReceipt;
